import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { WorshipReport } from '../../types/worshipReport';
import { FinanceReport } from '../../types/financeReport';
import { StatCard } from './StatCard';
import { FileSpreadsheet } from 'lucide-react';
import { exportToXLSX } from '../../utils/exportData';
import { RecentReports } from './RecentReports';
import { WorshipReportPreview } from '../WorshipReports/WorshipReportPreview';
import { CategoryChart } from './CategoryChart';
import { Users, UserPlus, Coins, TrendingUp } from 'lucide-react';
import { formatNumber } from '../../utils/format';
import { calculateStats } from '../../utils/stats';
import { MeetingTypeSelect } from '../ui/MeetingTypeSelect';
import { ConsolidatedView } from './ConsolidatedView';
import { LayoutGrid } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { subMonths, format } from 'date-fns';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'general', title: 'Évolution générale' },
  { id: 'adults', title: 'Évolution des Adultes' },
  { id: 'served', title: 'Évolution des Boss' },
  { id: 'children', title: 'Évolution des Enfants' },
  { id: 'men', title: 'Évolution des Hommes' },
  { id: 'women', title: 'Évolution des Femmes' }
] as const;

export default function Dashboard() {
  const { user } = useAuth();
  const isAdminOrPasteur = user?.role === 'admin' || user?.role === 'pasteur';
  const [activeTab, setActiveTab] = useState<'general' | 'consolidated'>('general');
  const [worshipReports, setWorshipReports] = useState<WorshipReport[]>([]);
  const [_financeReports, setFinanceReports] = useState<FinanceReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<WorshipReport | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<typeof CATEGORIES[number]['id']>('general');
  const [selectedMeetingType, setSelectedMeetingType] = useState('');
  const [startDate, setStartDate] = useState(format(subMonths(new Date(), 3), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isExporting, setIsExporting] = useState(false);
  const [stats, setStats] = useState({
    totalParticipants: 0,
    totalNewMembers: 0,
    totalFinances: 0,
    averageAttendance: 0,
    trends: {
      attendance: { value: 0, isPositive: true },
      newMembers: { value: 0, isPositive: true },
      finances: { value: 0, isPositive: true }
    }
  });

  useEffect(() => {
    const fetchReports = async () => {
      try {
        // Récupérer les rapports de culte récents
        const worshipQuery = query(collection(db, 'worshipReports'), orderBy('date', 'desc'), limit(5));
        const worshipSnapshot = await getDocs(worshipQuery);
        const worshipData = worshipSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as WorshipReport[];
        
        // Récupérer les rapports financiers récents
        const financeQuery = query(collection(db, 'financeReports'), orderBy('date', 'desc'), limit(10));
        const financeSnapshot = await getDocs(financeQuery);
        const financeData = financeSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as FinanceReport[];
        
        setWorshipReports(worshipData);
        setFinanceReports(financeData);
        setStats(calculateStats(worshipData, financeData));
      } catch (error) {
        console.error('Error fetching reports:', error);
      }
    };

    fetchReports();
  }, []);

  const filteredWorshipReports = selectedMeetingType
    ? worshipReports.filter(report => report.meetingTypeId === selectedMeetingType)
    : worshipReports;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      // Récupérer les rapports de culte
      let worshipQuery = query(
        collection(db, 'worshipReports'),
        where('date', '>=', start.toISOString()),
        where('date', '<=', end.toISOString()),
        orderBy('date', 'asc')
      );
      
      // Récupérer les rapports financiers
      let financeQuery = query(
        collection(db, 'financeReports'),
        where('date', '>=', start.toISOString()),
        where('date', '<=', end.toISOString()),
        orderBy('date', 'asc')
      );

      if (selectedMeetingType) {
        worshipQuery = query(
          collection(db, 'worshipReports'),
          where('date', '>=', start.toISOString()),
          where('date', '<=', end.toISOString()),
          where('meetingTypeId', '==', selectedMeetingType),
          orderBy('date', 'asc')
        );
        
        financeQuery = query(
          collection(db, 'financeReports'),
          where('date', '>=', start.toISOString()),
          where('date', '<=', end.toISOString()),
          where('meetingTypeId', '==', selectedMeetingType),
          orderBy('date', 'asc')
        );
      }

      const [worshipSnapshot, financeSnapshot] = await Promise.all([
        getDocs(worshipQuery),
        getDocs(financeQuery)
      ]);
      
      const worshipData = worshipSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as WorshipReport[];
      
      const financeData = financeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FinanceReport[];

      exportToXLSX(worshipData, financeData, startDate, endDate);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Erreur lors de l\'exportation des données');
    } finally {
      setIsExporting(false);
    }
  };
  return (
    <div className="max-w-7xl mx-auto space-y-3 sm:space-y-4 md:space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">Tableau de bord</h1>
        {isAdminOrPasteur && (
          <div className="flex rounded-lg border border-gray-200 bg-gray-100 p-1 gap-1">
            <button
              onClick={() => setActiveTab('general')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                activeTab === 'general'
                  ? 'bg-white text-[#00665C] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Vue générale
            </button>
            <button
              onClick={() => setActiveTab('consolidated')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                activeTab === 'consolidated'
                  ? 'bg-white text-[#00665C] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutGrid size={14} />
              Vue consolidée
            </button>
          </div>
        )}
      </div>
      
      {activeTab === 'consolidated' && isAdminOrPasteur ? (
        <ConsolidatedView />
      ) : (
        <>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <StatCard
          title="Participation moyenne"
          value={stats.totalParticipants}
          icon={<Users className="h-5 w-5 md:h-6 md:w-6" />}
          borderColor="#3B82F6"
          trend={stats.trends.attendance}
        />
        <StatCard
          title="Nouveaux membres"
          value={stats.totalNewMembers}
          icon={<UserPlus className="h-5 w-5 md:h-6 md:w-6" />}
          borderColor="#10B981"
          trend={stats.trends.newMembers}
        />
        <StatCard
          title="Moyenne présence"
          value={Math.round(stats.averageAttendance)}
          icon={<TrendingUp className="h-5 w-5 md:h-6 md:w-6" />}
          borderColor="#F59E0B"
        />
        {(user?.role === 'admin' || user?.role === 'pasteur') && (
        <StatCard
          title="Total entrées"
          value={`${formatNumber(stats.totalFinances)} FCFA`}
          icon={<Coins className="h-5 w-5 md:h-6 md:w-6" />}
          borderColor="#8B5CF6"
          trend={stats.trends.finances}
        />
        )}
      </div>

      <div className="space-y-3 sm:space-y-4 md:space-y-6">
        {/* Chart Section */}
        <div className="bg-white p-3 sm:p-4 md:p-5 lg:p-6 rounded-lg shadow-sm border border-gray-200 space-y-3 sm:space-y-4">
          <div className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <MeetingTypeSelect
                  value={selectedMeetingType}
                  onChange={setSelectedMeetingType}
                  required={false}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Date de début
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Date de fin
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className="w-full flex items-center justify-center px-4 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileSpreadsheet className="w-5 h-5 mr-2" />
                  {isExporting ? (
                    <span>Exportation...</span>
                  ) : (
                    <span>Exporter les données</span>
                  )}
                </button>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 -mx-3 px-3 sm:mx-0 sm:px-0 snap-x snap-mandatory">
              <div className="flex gap-2 min-w-max">
                {CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 touch-manipulation snap-start ${
                      selectedCategory === category.id
                        ? 'bg-[#00665C] text-white shadow-md scale-105'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-300'
                    }`}
                  >
                    {category.title}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[200px] sm:h-[250px] md:h-[300px] lg:h-[350px] w-full">
              <CategoryChart
                category={selectedCategory}
                title={CATEGORIES.find(c => c.id === selectedCategory)?.title || ''}
                meetingTypeId={selectedMeetingType}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          </div>
        </div>
        
        {/* Recent Reports Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <RecentReports 
            reports={filteredWorshipReports}
            onPreview={(report) => setSelectedReport(report)}
          />
        </div>
      </div>

        </>
      )}

      {selectedReport && (
        <WorshipReportPreview
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
        />
      )}
    </div>
  );
}